import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, List, ListItem, ListItemText, IconButton, Grid, Tooltip, Typography, CircularProgress } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon, Done as DoneIcon, Close as CloseIcon, Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon } from '@mui/icons-material';
import { useCreateOrganizationBrandMutation, useGetOrganizationBrandQuery, usePatchOrganizationBrandMutation } from '../features/api/rest/apiBrandOrganizationSlice';
import { useGetAlternativeNamesQuery, usePostAlternativeNamesMutation, usePatchAlternativeNameMutation, useDeleteAlternativeNameMutation } from '../features/api/rest/apiBrandAlternativeNamesSlice';
import Error from './Error';
import Spinner from './Spinner';

function BrandOrganizationDialog({ actionType, isOpen, setIsOpen, id = 0, oldName = '' }) {
  const { opk } = useParams();
  const { t } = useTranslation();
  let title = '';
  let handleConfirm = () => {};
  const [dialogFullscreen, setDialogFullscreen] = useState(false);
  const [dialogName, setDialogName] = useState(oldName || '');
  const [altItems, setAltItems] = useState([]);
  const [nameError, setNameError] = useState({
    error: false,
    text: '',
  });
  const [dialogAlt, setDialogAlt] = useState('');
  const [altError, setAltError] = useState({
    error: false,
    text: '',
  });
  const [editing, setEditing] = useState({
    index: -1,
    text: '',
  });
  const [editedEntries, setEditedEntries] = useState([]);
  const [editingError, setEditingError] = useState({
    error: false,
    text: '',
  });
  const [loading, setLoading] = useState(false);
  const [addBrand] = useCreateOrganizationBrandMutation();
  const [addAlternativeName] = usePostAlternativeNamesMutation();
  const [deleteAlternativeName] = useDeleteAlternativeNameMutation();
  const [patchAlternativeName] = usePatchAlternativeNameMutation();
  const [patchBrand] = usePatchOrganizationBrandMutation();
  const { brands, isLoading, isFetching, isError } = useGetOrganizationBrandQuery(
    { opk: opk },
    {
      selectFromResult: ({ data }) => ({
        brands: data?.results,
      }),
    }
  );
  const { alt, altId, isLoadingAlt, isFetchingAlt, isErrorAlt } =
    actionType === 'edit' && id
      ? useGetAlternativeNamesQuery(
          { opk: opk, id: id },
          {
            selectFromResult: ({ data }) => ({
              alt: data?.map((item) => item.name),
              altId: data?.map((item) => item.id),
            }),
          }
        )
      : { alt: [], isLoadingAlt: false, isFetchingAlt: false, isErrorAlt: false };
  useEffect(() => {
    if (isOpen) {
      setDialogFullscreen(false);
      setDialogName(oldName || '');
      setAltItems(alt || []);
      setNameError({
        error: false,
        text: '',
      });
      setAltError({
        error: false,
        text: '',
      });
      setDialogAlt('');
      setEditing({
        index: -1,
        text: '',
      });
      setEditingError({
        error: false,
        text: '',
      });
      setLoading(false);
    }
  }, [isOpen]);
  const validation = () => {
    if (dialogName === '') {
      setNameError({
        text: t('organizations.brand.dialog.error.name'),
        error: true,
      });
      return false;
    }
    if (actionType === 'edit') {
      if (brands && brands.some((brand) => brand.name === dialogName && brand.id !== id)) {
        setNameError({
          text: t('organizations.brand.dialog.error.nameExists'),
          error: true,
        });
        return false;
      }
    } else {
      if (brands && brands.some((brand) => brand.name === dialogName)) {
        setNameError({
          text: t('organizations.brand.dialog.error.nameExists'),
          error: true,
        });
        return false;
      }
    }
    return true;
  };
  const handleAdd = async () => {
    if (!validation()) return;
    setLoading(true);
    try {
      const response = await addBrand({ opk: opk, payload: { name: dialogName } });
      if (response.error) {
        throw new Error(response.error);
      }
      const errors = [];
      await Promise.all(
        altItems
          .filter((altItem) => altItem !== '')
          .map(async (altItem) => {
            const r = addAlternativeName({ opk, id: response.data.id, payload: { name: altItem } });
            if (r.error) {
              errors.push(r.error);
            }
          })
      );
      if (errors.length > 0) {
        throw new Error();
      }
      setIsOpen(false);
    } catch (error) {
      setNameError({
        text: t('organizations.brand.dialog.error.generic'),
        error: true,
      });
    } finally {
      setLoading(false);
    }
  };
  const handleEdit = async () => {
    if (!validation()) return;
    setLoading(true);
    try {
      const response = await patchBrand({ opk: opk, id: id, payload: { name: dialogName } });
      if (response.error) {
        throw new Error(response.error);
      }
      const errors = [];
      await Promise.all(
        altItems.map(async (altItem, index) => {
          if (altItem === '' && index < alt.length) {
            const r = deleteAlternativeName({ opk: opk, bpk: id, id: altId[index] });
            if (r.error) {
              errors.push(r.error);
            }
          }
        })
      );
      await Promise.all(
        editedEntries.map(async (index) => {
          if (index < alt.length && altItems[index] !== '' && altItems[index] !== alt[index]) {
            const r = patchAlternativeName({ opk: opk, bpk: id, id: altId[index], payload: { name: altItems[index] } });
            if (r.error) {
              errors.push(r.error);
            }
          }
        })
      );
      await Promise.all(
        altItems.slice(alt.length).map(async (altItem) => {
          if (altItem !== '') {
            const r = addAlternativeName({ opk: opk, id: id, payload: { name: altItem } });
            if (r.error) {
              errors.push(r.error);
            }
          }
        })
      );
      if (errors.length > 0) {
        throw new Error();
      }
      setIsOpen(false);
    } catch (error) {
      setNameError({
        text: t('organizations.brand.dialog.error.generic'),
        error: true,
      });
    } finally {
      setLoading(false);
    }
  };
  const handleDialogNameChange = (e) => {
    setDialogName(e.target.value);
    if (nameError.error) {
      setNameError({
        ...nameError,
        error: false,
      });
    }
  };
  const handleNewAltChange = (e) => {
    setDialogAlt(e.target.value);
    if (altError.error) {
      setAltError({
        ...altError,
        error: false,
      });
    }
  };
  const handleNewAlt = () => {
    if (dialogAlt === '') {
      setAltError({
        text: t('organizations.brand.dialog.error.alt'),
        error: true,
      });
      return;
    }
    if (altItems && altItems.some((item) => item === dialogAlt)) {
      setAltError({
        text: t('organizations.brand.dialog.error.altExists'),
        error: true,
      });
      return false;
    }
    setAltItems([...altItems, dialogAlt]);
    setDialogAlt('');
  };
  const handleEditAlt = (index) => {
    setEditingError({
      ...editingError,
      error: false,
    });
    setEditing({
      text: altItems[index],
      index: index,
    });
  };
  const handleEditAltChange = (e) => {
    setEditing({
      ...editing,
      text: e.target.value,
    });
    if (editingError.error) {
      setEditingError({
        ...editingError,
        error: false,
      });
    }
  };
  const handleEditAltClose = () => {
    setEditing({
      text: '',
      index: -1,
    });
  };
  const handleEditAltSave = (index) => {
    console.log(editing.index);
    if (editing.text === '') {
      setEditingError({
        text: t('organizations.brand.dialog.error.alt'),
        error: true,
      });
      return;
    }
    if (altItems && altItems.some((item, index) => item === editing.text && index !== editing.index)) {
      setEditingError({
        text: t('organizations.brand.dialog.error.altExists'),
        error: true,
      });
      return;
    }
    if (editing.text !== altItems[index] && !editedEntries.includes(index)) {
      setEditedEntries([...editedEntries, index]);
    }
    const updatedAltItems = [...altItems];
    updatedAltItems[index] = editing.text;
    setAltItems(updatedAltItems);
    setEditing({
      text: '',
      index: -1,
    });
  };
  const handleDeleteAlt = (index) => {
    const updatedAltItems = [...altItems];
    updatedAltItems[index] = '';
    setAltItems(updatedAltItems);
  };
  switch (actionType) {
    case 'add':
      title = t('organizations.brand.add');
      handleConfirm = handleAdd;
      break;
    case 'edit':
      title = t('organizations.brand.edit');
      handleConfirm = handleEdit;
      break;
    default:
      title = '';
  }

  if (isLoading || isFetching || isLoadingAlt || isFetchingAlt) return <Spinner />;

  if (isError || isErrorAlt) return <Error />;

  return (
    <Dialog fullWidth open={isOpen} onClose={() => setIsOpen(false)} fullScreen={dialogFullscreen}>
      <DialogTitle sx={{ textAlign: 'center' }}>
        <Tooltip title={dialogFullscreen ? t('organizations.brand.dialog.exitFullscreen') : t('organizations.brand.dialog.fullscreen')} placement="right">
          <IconButton onClick={() => setDialogFullscreen(!dialogFullscreen)} size="large" sx={{ position: 'absolute', top: 8, left: 8 }}>
            {dialogFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
        {title}
      </DialogTitle>
      <DialogContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <TextField
          fullWidth
          required
          value={dialogName}
          id="brandname"
          sx={{ mt: 2 }}
          error={nameError.error}
          helperText={nameError.error ? nameError.text : ''}
          label={t('organizations.brand.dialog.name')}
          name="brandname"
          inputProps={{ 'brand-data': 'name' }}
          onChange={handleDialogNameChange}
        />
        {altItems.length > 0 && (
          <Typography variant="h6" sx={{ mt: 3 }}>
            {t('organizations.brand.alternatives')}
          </Typography>
        )}
        <List sx={{ overflowY: 'auto' }}>
          {altItems &&
            altItems.map(
              (item, index) =>
                item !== '' && (
                  <ListItem key={index}>
                    {editing.index === index ? (
                      <Grid container sx={{ p: 0 }} spacing={1} alignItems="center">
                        <Grid item xs>
                          <TextField
                            value={editing.text}
                            sx={{ width: '100%' }}
                            error={editingError.error}
                            helperText={editingError.error ? editingError.text : ''}
                            label={t('common.actions.edit')}
                            onChange={handleEditAltChange}
                            onKeyUp={(e) => {
                              if (e.key === 'Enter') handleEditAltSave(index);
                            }}
                          />
                        </Grid>
                        <Grid item>
                          <Tooltip title={t('common.actions.close')} placement="top">
                            <IconButton onClick={handleEditAltClose}>
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.actions.save')} placement="top">
                            <IconButton onClick={() => handleEditAltSave(index)}>
                              <DoneIcon />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid container alignItems="center">
                        <Grid item xs>
                          <ListItemText
                            primary={item}
                            sx={{
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: '3',
                              WebkitBoxOrient: 'vertical',
                              textAlign: 'initial',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                            }}
                          />
                        </Grid>
                        <Grid item>
                          <Tooltip title={t('common.actions.edit')} placement="top">
                            <IconButton onClick={() => handleEditAlt(index)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.actions.delete')} placement="top">
                            <IconButton onClick={() => handleDeleteAlt(index)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    )}
                  </ListItem>
                )
            )}
        </List>
        <Grid container spacing={1} display="flex" alignItems="center" sx={{ mt: 1 }}>
          <Grid item xs>
            <TextField
              fullWidth
              value={dialogAlt}
              id="brandname"
              error={altError.error}
              helperText={altError.error ? altError.text : ''}
              label={t('organizations.brand.dialog.alternative')}
              onChange={handleNewAltChange}
              autoComplete="off"
              onKeyUp={(e) => {
                if (e.key === 'Enter') handleNewAlt();
              }}
            />
          </Grid>
          <Grid item>
            <Tooltip title={t('organizations.brand.dialog.addAlternative')} placement="top">
              <IconButton onClick={handleNewAlt} size="large">
                <AddIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogContent>
      {loading ? (
        <Grid container display="flex" justifyContent="center">
          <CircularProgress />
        </Grid>
      ) : null}
      <DialogActions sx={{ p: 3 }}>
        <Grid container display="flex" justifyContent="flex-end" gap={1}>
          <Grid item>
            <Button type="submit" variant="outlined" color="secondary" onClick={() => setIsOpen(false)}>
              {t('common.actions.close')}
            </Button>
          </Grid>
          <Grid item>
            <Button disabled={loading} type="submit" variant="contained" color="primary" onClick={handleConfirm}>
              {t('common.actions.save')}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

BrandOrganizationDialog.propTypes = {
  actionType: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  id: PropTypes.number,
  oldName: PropTypes.string,
};

export default BrandOrganizationDialog;
